import { InternalId } from '@edgebox/data-definition-kit';
import { faTrafficLight } from '@fortawesome/pro-light-svg-icons/faTrafficLight';
import { faFlaskVial } from '@fortawesome/pro-light-svg-icons/faFlaskVial';
import { faComputer } from '@fortawesome/pro-light-svg-icons/faComputer';
import { faIndustryWindows } from '@fortawesome/pro-light-svg-icons/faIndustryWindows';
import { faShieldCheck } from '@fortawesome/pro-light-svg-icons/faShieldCheck';
import { faCircleQuestion } from '@fortawesome/pro-light-svg-icons/faCircleQuestion';
import { SiteEnvironmentType } from '@edgebox/sync-core-data-definitions';
import { FontAwesomeIcon, FontAwesomeIconProps } from '@fortawesome/react-fontawesome';
import React, { PropsWithChildren, useContext, useEffect, useState } from 'react';
import { SyncCoreApiContext } from '../contexts/SyncCoreApiContext';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { Nugget } from './Nugget';
import { ClientSiteEntity } from '@edgebox/sync-core-rest-client';
import { isPlatformGovernanceSite, siteKindLabel, UNRESOLVED_SITE_LABEL } from './siteKind.utils';

/**
 * The platform's own source of terms runs on no application and in no environment, so it is drawn
 * with a mark of its own instead of an application or environment icon.
 */
export const PLATFORM_GOVERNANCE_SITE_ICON = faShieldCheck;

/**
 * A site a reference names that this session cannot read. Nothing is known about it, so it is drawn
 * as the open question it is — never as the application the caller guessed it runs on.
 */
export const UNRESOLVED_SITE_ICON = faCircleQuestion;

export const SITE_ENVIRONMENT_TYPE_ICONS = {
  [SiteEnvironmentType.Local]: faComputer,
  [SiteEnvironmentType.Testing]: faFlaskVial,
  [SiteEnvironmentType.Staging]: faTrafficLight,
  [SiteEnvironmentType.Production]: faIndustryWindows,
};
export function SiteEnvironmentIcon({ type, ...rest }: Omit<FontAwesomeIconProps, 'icon'> & { type: SiteEnvironmentType }) {
  return <FontAwesomeIcon {...rest} icon={SITE_ENVIRONMENT_TYPE_ICONS[type]} />;
}
export function EnvironmentIconForSite({
  siteId,
  fallbackIcon,
  ...rest
}: Omit<FontAwesomeIconProps, 'icon'> & { siteId: InternalId; fallbackIcon: IconProp }) {
  const { site, unresolved } = useSiteForIcon(siteId);

  if (site && isPlatformGovernanceSite(site)) {
    return <FontAwesomeIcon {...rest} icon={PLATFORM_GOVERNANCE_SITE_ICON} />;
  }

  // A site that cannot be read is drawn as the open question it is: the caller's icon names an
  // application, and this site may be running none.
  if (unresolved) {
    return <FontAwesomeIcon {...rest} icon={UNRESOLVED_SITE_ICON} />;
  }

  // Until the site is there, and for a site that names no environment, the caller's icon stands in.
  if (!site?.environmentType) {
    return <FontAwesomeIcon {...rest} icon={fallbackIcon} />;
  }

  return <SiteEnvironmentIcon {...rest} type={site.environmentType} />;
}

/**
 * The site an icon is drawn for. Another site means the mark of the one before it says nothing, so
 * the site goes away until the new one is there and a read that lands after another change is
 * dropped; a read that fails leaves `unresolved`, which every caller draws rather than waiting.
 */
function useSiteForIcon(siteId: InternalId) {
  const api = useContext(SyncCoreApiContext);
  const [site, setSite] = useState<ClientSiteEntity | undefined>();
  const [unresolved, setUnresolved] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setSite(undefined);
    setUnresolved(false);
    api?.api.billing.sites
      .item(siteId)
      .then((loaded) => {
        if (!cancelled) {
          setSite(loaded);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setUnresolved(true);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [api, siteId]);

  return { site, unresolved };
}
export function EnvironmentIconForSiteAsNugget({
  siteId,
  fallbackIcon,
  children,
  dark,
}: { siteId: InternalId; fallbackIcon: IconProp; dark?: boolean } & PropsWithChildren) {
  const { site, unresolved } = useSiteForIcon(siteId);

  if (unresolved) {
    return (
      <Nugget dark={dark} icon={UNRESOLVED_SITE_ICON}>
        {children ?? UNRESOLVED_SITE_LABEL}
      </Nugget>
    );
  }

  if (!site) {
    return (
      <Nugget dark={dark} icon={fallbackIcon}>
        {children}
      </Nugget>
    );
  }

  if (isPlatformGovernanceSite(site)) {
    return (
      <Nugget dark={dark} icon={PLATFORM_GOVERNANCE_SITE_ICON}>
        {children ?? siteKindLabel(site)}
      </Nugget>
    );
  }

  return (
    <Nugget dark={dark} icon={SITE_ENVIRONMENT_TYPE_ICONS[site.environmentType]}>
      {children ?? site.name}
    </Nugget>
  );
}
